import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  useState Hook — Local State Management
//  Chapter 3 · State & Events
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `import { useState } from 'react';

// Syntax: const [value, setValue] = useState(initialValue);
const [count,    setCount]    = useState(0);
const [name,     setName]     = useState('');
const [isOpen,   setIsOpen]   = useState(false);
const [items,    setItems]    = useState([]);
const [user,     setUser]     = useState(null);
const [settings, setSettings] = useState({ theme: 'dark', lang: 'en' });`;

const CODE_2 = `function Counter() {
  const [count, setCount] = useState(0);

  // ✅ Simple update
  const increment = () => setCount(count + 1);

  // ✅ Functional update — safe when new state depends on previous
  const safeIncrement = () => setCount(prev => prev + 1);

  // ✅ Triple-click safe — all three updates use latest value
  const incrementBy3 = () => {
    setCount(prev => prev + 1);
    setCount(prev => prev + 1);
    setCount(prev => prev + 1);
  };

  // ❌ WRONG — only increments once (stale closure)
  const badIncrementBy3 = () => {
    setCount(count + 1);  // All three read same stale 'count'
    setCount(count + 1);
    setCount(count + 1);
  };

  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={safeIncrement}>+1</button>
      <button onClick={incrementBy3}>+3</button>
      <button onClick={() => setCount(0)}>Reset</button>
    </div>
  );
}`;

const CODE_3 = `function ProfileEditor() {
  const [profile, setProfile] = useState({
    name: 'Alice', email: 'alice@test.com', bio: '', isPublic: true
  });
  const [tags, setTags] = useState(['react', 'javascript']);

  // ✅ Update one field — spread existing, override target field
  const updateField = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  // ✅ Add to array — spread + new item
  const addTag = (tag) => setTags(prev => [...prev, tag]);

  // ✅ Remove from array — filter (no mutation)
  const removeTag = (tag) => setTags(prev => prev.filter(t => t !== tag));

  // ✅ Update item in array — map to create new array
  const updateTag = (oldTag, newTag) =>
    setTags(prev => prev.map(t => t === oldTag ? newTag : t));

  // ❌ WRONG — mutating state object directly
  const badUpdate = () => {
    profile.name = 'Bob';   // React won't re-render!
    setProfile(profile);    // Same reference = no update
  };
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function UseStateHookLocalState() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 3 · State & Events</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>useState Hook — Local State Management</h1>
        <p style={styles.desc}>Master React's primary state hook and understand how re-renders work.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── What is State? ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>What is State?</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "State is data that can change over time within a component. When state changes, React automatically re-renders the component and its children to reflect the new data. State is <b>private</b> to the component that owns it — unlike props which come from outside." }} />
      </div>

      /* ── useState Syntax & Rules ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>useState Syntax & Rules</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "useState returns an array with exactly two items: the current value and a setter function. Always destructure them with meaningful names." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Updating State Correctly ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Updating State Correctly</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "Never mutate state directly. Always use the setter. When new state depends on previous state, use the <b>functional update form</b> to avoid stale closure bugs." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── State with Objects & Arrays ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>State with Objects & Arrays</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "When state is an object or array, you <b>must spread the previous state</b> to create a new reference. React uses referential equality to detect changes." }} />
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_3, 2)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 2 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_3)}</pre>
        </div>
      </div>

      /* ── Practice Exercise ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Practice Exercise</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 PRACTICE EXERCISE</div>
          <div style={styles.qItem}>✦ Build a ShoppingList component that:</div>
          <div style={styles.qItem}>✦ 1. Has an input to type a new item</div>
          <div style={styles.qItem}>✦ 2. A button to add the item to the list</div>
          <div style={styles.qItem}>✦ 3. Each list item has a \'Remove\' button</div>
          <div style={styles.qItem}>✦ 4. Show the total count of items</div>
          <div style={styles.qItem}>✦ 5. A \'Clear All\' button to empty the list</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
